/**
 * AvatarCropDialog - кадрирование фото профиля перед загрузкой.
 *
 * Круглая рамка, перетаскивание мышью или пальцем, масштаб ползунком, превью в размерах профиля и шапки.
 * Результат - квадрат 512 px в WebP (JPEG, если браузер не умеет WebP); качество и размер снижаются,
 * пока файл не уложится в лимит сервера 300 КБ (PROJECT_NOTES §35).
 * Ошибку сохранения показывает в окне - окно остается открытым.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography
} from '@mui/material';
import { ZoomIn, ZoomOut } from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';

/** Лимит сервера на декодированный аватар */
const MAX_AVATAR_BYTES = 300 * 1024;
/** Исходный файл больше этого не открываем: браузер может не справиться с декодированием */
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
const VIEW = 260;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const OUTPUT_SIZES = [512, 384, 256];
const QUALITIES = [0.88, 0.8, 0.7, 0.6, 0.5];

interface Offset {
  x: number;
  y: number;
}

interface Layout {
  left: number;
  top: number;
  width: number;
  height: number;
  scale: number;
}

export interface AvatarCropDialogProps {
  /** Выбранный файл; null - окно закрыто */
  file: File | null;
  onClose: () => void;
  /** Сохранение готового data URL; ошибка остается в окне */
  onSave: (dataUrl: string) => Promise<void>;
}

const decodedBytes = (dataUrl: string): number => {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

const formatMegabytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(bytes / 1024))} КБ`;

/** Положение картинки в рамке VIEW при масштабе zoom и смещении центра offset */
function layoutOf(img: HTMLImageElement, zoom: number, offset: Offset): Layout {
  const scale = Math.max(VIEW / img.naturalWidth, VIEW / img.naturalHeight) * zoom;
  const width = img.naturalWidth * scale;
  const height = img.naturalHeight * scale;
  return {
    left: VIEW / 2 + offset.x - width / 2,
    top: VIEW / 2 + offset.y - height / 2,
    width,
    height,
    scale
  };
}

/** Смещение, при котором картинка закрывает рамку целиком */
function clampOffset(img: HTMLImageElement, zoom: number, offset: Offset): Offset {
  const { width, height } = layoutOf(img, zoom, { x: 0, y: 0 });
  const maxX = Math.max(0, (width - VIEW) / 2);
  const maxY = Math.max(0, (height - VIEW) / 2);
  return {
    x: Math.max(-maxX, Math.min(maxX, offset.x)),
    y: Math.max(-maxY, Math.min(maxY, offset.y))
  };
}

/** Кадр в квадрат size с белым фоном (прозрачные PNG в JPEG не чернеют) */
function renderCrop(img: HTMLImageElement, layout: Layout, size: number, type: string, quality: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не поддерживает обработку изображений');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  const sourceSize = VIEW / layout.scale;
  ctx.drawImage(img, -layout.left / layout.scale, -layout.top / layout.scale, sourceSize, sourceSize, 0, 0, size, size);
  return canvas.toDataURL(type, quality);
}

/** Самый качественный вариант, который укладывается в лимит сервера */
function exportAvatar(img: HTMLImageElement, layout: Layout): string {
  const probe = renderCrop(img, layout, 8, 'image/webp', 0.8);
  const type = probe.startsWith('data:image/webp') ? 'image/webp' : 'image/jpeg';
  for (let s = 0; s < OUTPUT_SIZES.length; s++) {
    for (let q = 0; q < QUALITIES.length; q++) {
      const dataUrl = renderCrop(img, layout, OUTPUT_SIZES[s], type, QUALITIES[q]);
      if (decodedBytes(dataUrl) <= MAX_AVATAR_BYTES) return dataUrl;
    }
  }
  throw new Error('Не удалось сжать фото до 300 КБ. Выберите другое изображение');
}

const AvatarCropDialog: React.FC<AvatarCropDialogProps> = ({ file, onClose, onSave }) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; origin: Offset } | null>(null);

  useEffect(() => {
    setImage(null);
    setImageSrc(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Выбранный файл не является изображением. Выберите JPEG, PNG или WebP');
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError(`Файл слишком большой (${formatMegabytes(file.size)}). Выберите изображение до 20 МБ`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      setImage(img);
      setImageSrc(url);
    };
    img.onerror = () => {
      if (!cancelled) setError('Не удалось прочитать изображение. Выберите JPEG, PNG или WebP');
    };
    img.src = url;
    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const layout = image ? layoutOf(image, zoom, offset) : null;

  const changeZoom = (next: number) => {
    if (!image) return;
    const value = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    // Точка в центре рамки остается в центре
    const ratio = value / zoom;
    setZoom(value);
    setOffset(clampOffset(image, value, { x: offset.x * ratio, y: offset.y * ratio }));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!image || saving) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, origin: offset };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!image || !drag || drag.pointerId !== e.pointerId) return;
    setOffset(clampOffset(image, zoom, {
      x: drag.origin.x + e.clientX - drag.startX,
      y: drag.origin.y + e.clientY - drag.startY
    }));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current && dragRef.current.pointerId === e.pointerId) dragRef.current = null;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!image) return;
    const step = e.shiftKey ? 30 : 10;
    const moves: Record<string, Offset> = {
      ArrowLeft: { x: step, y: 0 },
      ArrowRight: { x: -step, y: 0 },
      ArrowUp: { x: 0, y: step },
      ArrowDown: { x: 0, y: -step }
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    setOffset(clampOffset(image, zoom, { x: offset.x + move.x, y: offset.y + move.y }));
  };

  const handleSave = async () => {
    if (!image || !layout) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(exportAvatar(image, layout));
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось сохранить фото'));
    } finally {
      setSaving(false);
    }
  };

  /** Картинка в круге размера size: та же раскладка, что в рамке, в масштабе size / VIEW */
  const renderLayer = (size: number) => {
    if (!layout || !imageSrc) return null;
    const k = size / VIEW;
    return (
      <Box
        component="img"
        src={imageSrc}
        alt=""
        draggable={false}
        sx={{
          position: 'absolute',
          left: layout.left * k,
          top: layout.top * k,
          width: layout.width * k,
          height: layout.height * k,
          maxWidth: 'none',
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      />
    );
  };

  const preview = (size: number, label: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          bgcolor: '#f1f5f9',
          border: '1px solid #e2e8f0'
        }}
      >
        {renderLayer(size)}
      </Box>
      <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
    </Box>
  );

  return (
    <Dialog
      open={Boolean(file)}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          fontSize: '1.05rem',
          fontWeight: 700,
          color: '#0f172a'
        }}
      >
        Фото профиля
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Box sx={{ pt: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: { xs: 'center', sm: 'flex-start' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: VIEW, flexShrink: 0 }}>
            <Box
              role="img"
              aria-label="Область кадрирования: перетащите фото или используйте стрелки"
              tabIndex={0}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onKeyDown={handleKeyDown}
              sx={{
                position: 'relative',
                width: VIEW,
                height: VIEW,
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: '#0f172a',
                cursor: image ? 'grab' : 'default',
                '&:active': { cursor: image ? 'grabbing' : 'default' },
                touchAction: 'none',
                outline: 'none',
                '&:focus-visible': { boxShadow: `0 0 0 3px ${ZERDE_COLORS.primary}` }
              }}
            >
              {renderLayer(VIEW)}
              {/* Затемнение вокруг круга - то, что не войдет в аватар */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  pointerEvents: 'none'
                }}
              />
              {!image && !error && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={28} sx={{ color: '#ffffff' }} />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" aria-label="Уменьшить" disabled={!image || zoom <= MIN_ZOOM} onClick={() => changeZoom(zoom - 0.25)}>
                <ZoomOut fontSize="small" />
              </IconButton>
              <Slider
                size="small"
                aria-label="Масштаб"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                disabled={!image}
                onChange={(_, value) => changeZoom(Array.isArray(value) ? value[0] : value)}
                sx={{ color: ZERDE_COLORS.primary }}
              />
              <IconButton size="small" aria-label="Увеличить" disabled={!image || zoom >= MAX_ZOOM} onClick={() => changeZoom(zoom + 0.25)}>
                <ZoomIn fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
              {preview(96, 'Профиль')}
              {preview(28, 'Шапка')}
            </Box>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              Перетащите фото, чтобы выбрать область, и настройте масштаб. В профиль попадет только круг.
            </Typography>
            {file && (
              <Typography variant="caption" sx={{ color: '#94a3b8', wordBreak: 'break-all' }}>
                Исходный файл: {file.name}, {formatMegabytes(file.size)}. Фото будет сжато до 300 КБ.
              </Typography>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: '10px' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}>
          Отмена
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={!image || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: ZERDE_COLORS.primary }}
        >
          {saving ? 'Сохранение...' : 'Сохранить фото'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCropDialog;
